import { logger } from "./logger";
import { sleep } from "./sleep";

export interface UserbackWidget {
	show: () => void;
	hide: () => void;
	destroy: () => void;
	openForm: () => void;
	isLoaded: () => boolean;
}

interface UserData {
	id: string;
	name?: string;
	email?: string;
	phone?: string;
}

let userbackInstance: UserbackWidget | null = null;
let isLoading = false;
let loadPromise: Promise<UserbackWidget> | null = null;

/**
 * Loads the Userback script and initializes it with user data
 * Returns a promise that resolves to the Userback widget instance
 */
export async function loadUserback(userData?: UserData): Promise<UserbackWidget> {
	// Return existing instance if already loaded
	if (userbackInstance) {
		return userbackInstance;
	}

	// Return existing promise if already loading
	if (loadPromise) {
		return loadPromise;
	}

	isLoading = true;

	loadPromise = new Promise((resolve, reject) => {
		// Check if Userback is already loaded
		if (window.Userback) {
			initializeUserback(userData).then(resolve).catch(reject);
			return;
		}

		// Create and load the Userback script
		const script = document.createElement("script");
		script.src = "https://static.userback.io/widget/v1.js";
		script.async = true;
		script.onload = () => initializeUserback(userData).then(resolve).catch(reject);
		script.onerror = () => {
			isLoading = false;
			loadPromise = null;
			reject(new Error("Failed to load Userback script"));
		};

		document.head.appendChild(script);
	});

	try {
		const instance = await loadPromise;
		isLoading = false;
		return instance;
	} catch (error) {
		isLoading = false;
		loadPromise = null;
		throw error;
	}
}

/**
 * Initializes Userback with the provided configuration and user data
 */
async function initializeUserback(userData?: UserData): Promise<UserbackWidget> {
	if (!window.Userback) {
		throw new Error("Userback script not loaded");
	}

	try {
		// Initialize Userback with configuration using the init method
		window.Userback.init("P-gnqdmpx12I86ZQpgMllz5GLLf", {
			// Set user data if provided
			...(userData && {
				user_data: {
					id: userData.id,
					info: {
						name: userData.name,
						email: userData.email,
						phone: userData.phone,
					},
				},
			}),
			autohide: true,
			widget_settings: {
				position: "w",
				device_type: "desktop",
				help_title: "Feedback",
				help_message: "Feedback",
			},
		});

		// Return the Userback object as the widget instance
		userbackInstance = window.Userback;
		return window.Userback;
	} catch (error) {
		throw error;
	}
}

/**
 * Opens the Userback feedback form, loading the script if necessary
 * Implements retry mechanism for slow network conditions
 */
export async function openUserbackForm(userData?: UserData): Promise<UserbackWidget | undefined> {
	try {
		const widget = await loadUserback(userData);

		// Retry mechanism for opening the form - especially useful for slow networks
		const maxRetries = 10; // 10 seconds total
		const retryInterval = 1000; // 1 second between retries

		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			if (widget && typeof widget.isLoaded === "function" && widget.isLoaded()) {
				widget.openForm();
				return widget;
			}

			await sleep(retryInterval);
		}

		// All retries failed
		logger.debug(`Failed to open Userback form after ${maxRetries} attempts over ${maxRetries} seconds`);
		return widget;
	} catch (error) {
		logger.debug("Failed to load Userback widget:", error);
	}
}

/**
 * Returns the current Userback instance if loaded, null otherwise
 */
export function getUserbackInstance(): UserbackWidget | null {
	return userbackInstance;
}

/**
 * Returns whether Userback is currently loading
 */
export function isUserbackLoading(): boolean {
	return isLoading;
}
